import React, { useState, useEffect, useRef, useCallback } from 'react';
import { collection, query, where, getDocs, orderBy, addDoc, serverTimestamp, onSnapshot, doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useAuth } from '../../context/AuthContext';
import messageService from '../../services/messageService';
import CSRFToken from '../../security/CSRFToken';
import { sanitizeText, sanitizeHtml } from '../../security/sanitize';

import LoadingSpinner from '../../components/LoadingSpinner';
import { useFeedback } from '../../contexts/FeedbackContext';

// Icons
import {
  Send,
  Paperclip,
  Smile,
  Search,
  ChevronDown,
  CircleUser
} from 'lucide-react';

const Messages = () => {
  const { currentUser } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [activeConversation, setActiveConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const { showSuccess, showError, handleAsyncOperation } = useFeedback();
  const [showGuidelines, setShowGuidelines] = useState(false);
  const messagesEndRef = useRef(null);

    // No placeholder data needed - using Firestore

  useEffect(() => {
    const fetchConversations = async () => {
      if (!currentUser) return;

      try {
        // Fetch conversations from Firestore
        const conversations = await messageService.getConversations(currentUser.uid);
        
        // Enhance conversation data with brand and campaign details
        const enhancedConversations = await Promise.all(conversations.map(async (conversation) => {
          // Determine if the other participant is a brand (assuming brands have role='brand' in users collection)
          const otherParticipantId = conversation.participants.find(id => id !== currentUser.uid);
          
          if (!otherParticipantId) return conversation;
          
          // Get user data for the other participant
          const userRef = doc(db, 'users', otherParticipantId);
          const userSnap = await getDoc(userRef);
          
          if (!userSnap.exists()) return conversation;
          
          const userData = userSnap.data();
          
          // If there's a campaign associated with this conversation, get its details
          let campaignData = {};
          if (conversation.campaignId) {
            const campaignRef = doc(db, 'campaigns', conversation.campaignId);
            const campaignSnap = await getDoc(campaignRef);
            
            if (campaignSnap.exists()) {
              campaignData = campaignSnap.data();
            }
          }
          
          // Determine if the other participant is a brand
          // We need a more robust detection mechanism since the role/userType fields might be inconsistent
          // For influencer view, we assume the other participant is a brand if any of these conditions are true:
          // 1. They have role or userType set to 'brand'
          // 2. They have brandName or companyName fields
          // 3. If we're in a conversation where the current user is an influencer, assume the other is a brand
          const hasBrandRole = userData.role === 'brand' || userData.userType === 'brand';
          const isCurrentUserInfluencer = currentUser?.userType === 'influencer' || currentUser?.role === 'influencer';
          
          // For the influencer view, we want to be more aggressive in identifying brands
          const isBrand = hasBrandRole || isCurrentUserInfluencer;
          // Brand detection complete
          
          // Get the best possible name for the other participant
          // For influencer view, we want to show brand names
          // Check if the other participant is a brand and use appropriate name fields
          let participantName;
          
          if (isBrand) {
            // If it's a brand, use brand-specific fields first
            participantName = userData.brandName || userData.companyName || userData.displayName || 
                            userData.username || userData.name || userData.email?.split('@')[0] || 'Brand';
          } else {
            // For non-brands, use generic name fields
            participantName = userData.displayName || userData.username || 
                            userData.name || userData.email?.split('@')[0] || 'User';
          }
          // Participant data processed
          
          // Image fields processed
          
          // For influencer view, we always want to treat the other participant as a brand
          // This ensures the UI displays correctly regardless of data inconsistencies
          return {
            ...conversation,
            brandId: otherParticipantId, // Always store the other participant ID for messaging
            isBrand: true, // Always treat as brand in influencer view
            brandName: participantName || 'Brand', // Always use participant name as brand name
            otherParticipantName: participantName || 'Brand', // Store the actual participant name separately
            brandLogo: userData.profilePicture || userData.profileImageUrl || userData.brandLogo || userData.logo || userData.photoURL || userData.avatar || userData.image || userData.profileImage || userData.brandImage || '/assets/default-avatar.png',
            campaignTitle: campaignData.title || 'General Conversation',
            unreadCount: conversation.unreadCounts?.[currentUser.uid] || 0,
            lastMessage: {
              text: conversation.lastMessageText || '',
              timestamp: conversation.lastMessageSentAt ? new Date(conversation.lastMessageSentAt.toDate()).toISOString() : new Date().toISOString(),
              senderId: conversation.lastMessageSenderId || ''
            }
          };
        }));
        
        // For influencer view, show all conversations but prioritize those with brandId
        // This ensures we don't miss any messages even if brandId detection has issues

        
        // Sort conversations to prioritize those with brandId, but show all
        const sortedConversations = [...enhancedConversations].sort((a, b) => {
          // First prioritize conversations with brandId
          if (a.brandId && !b.brandId) return -1;
          if (!a.brandId && b.brandId) return 1;
          
          // Then sort by most recent message
          return new Date(b.lastMessage.timestamp) - new Date(a.lastMessage.timestamp);
        });
        
        setConversations(sortedConversations);

      } catch (error) {
        console.error("Error fetching conversations:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchConversations();
    
    // Set up real-time listener for new conversations
    if (currentUser) {
      const conversationsRef = collection(db, 'conversations');
      const q = query(
        conversationsRef,
        where('participants', 'array-contains', currentUser.uid),
        orderBy('lastMessageSentAt', 'desc')
      );
      
      const unsubscribe = onSnapshot(q, async (snapshot) => {
        if (!snapshot.empty) {
          fetchConversations(); // Refresh conversations when changes occur
        }
      }, (error) => {
        console.error("Error listening to conversations:", error);
      });
      
      return () => unsubscribe();
    }
  }, [currentUser]);

  useEffect(() => {
    // Scroll to bottom when messages change
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (!activeConversation) return;
    
    // Set up real-time listener for messages in the active conversation
    const messagesRef = collection(db, 'messages');
    const q = query(
      messagesRef,
      where('conversationId', '==', activeConversation.id),
      where('isDeleted', '==', false),
      orderBy('sentAt', 'asc')
    );
    
    const unsubscribe = onSnapshot(q, async (snapshot) => {
      const messagesList = [];
      
      // Process each message document
      snapshot.forEach((doc) => {
        const messageData = doc.data();
        const sentAt = messageData.sentAt ? new Date(messageData.sentAt.toDate()).toISOString() : new Date().toISOString();
        
        messagesList.push({
          id: doc.id,
          text: messageData.text,
          timestamp: sentAt,
          senderId: messageData.senderId,
          // For sender name, use 'You' for current user, otherwise use the brand name if it's a brand conversation
          // or the other participant name if it's not a brand conversation
          senderName: messageData.senderId === currentUser.uid ? 'You' : 
                     (activeConversation.isBrand ? activeConversation.brandName : activeConversation.otherParticipantName) || 'Brand',
          attachments: messageData.attachments || []
        });
      });
      
      setMessages(messagesList);
      
      // Mark messages as read if they're from the other person
      if (messagesList.some(msg => msg.senderId !== currentUser.uid)) {
        try {
          await messageService.markMessagesAsRead(activeConversation.id, currentUser.uid);
        } catch (error) {
          console.error('Error marking messages as read:', error);
        }
      }
    }, (error) => {
      console.error('Error listening to messages:', error);
    });
    
    return () => unsubscribe();
  }, [activeConversation, currentUser]);

  const handleConversationSelect = async (conversation) => {
    setActiveConversation(conversation);
    
    try {
      // Mark messages as read in Firestore
      if (conversation.unreadCount > 0) {
        await messageService.markMessagesAsRead(conversation.id, currentUser.uid);
        
        // Update local state for immediate UI feedback
        const updatedConversations = conversations.map(conv => {
          if (conv.id === conversation.id) {
            return { ...conv, unreadCount: 0 };
          }
          return conv;
        });
        
        setConversations(updatedConversations);
      }
    } catch (error) {
      console.error('Error marking conversation as read:', error);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    
    // Get CSRF token from form
    const form = new FormData(e.target);
    const csrfToken = form.get('csrfToken');
    
    // Validate CSRF token
    if (!csrfToken || csrfToken !== sessionStorage.getItem('csrfToken')) {
      showError('Security validation failed.', 'Please refresh the page and try again.', 6000);
      return;
    }
    
    // Sanitize message content
    const sanitizedMessage = sanitizeText(newMessage);
    
    if (!sanitizedMessage.trim() || !activeConversation) return;
    
    // Clear any previous errors handled by new toast system
    
    try {
      // Get the other participant (brand)
      // If brandId is not available, try to find the other participant from the participants array
      let recipientId = activeConversation.brandId;
      
      if (!recipientId) {

        // Find the other participant ID (not the current user)
        const otherParticipantId = activeConversation.participants.find(id => id !== currentUser.uid);
        
        if (otherParticipantId) {

          recipientId = otherParticipantId;
        } else {
          showError('Cannot identify message recipient.', 'Please refresh and try again.', 6000);
          return;
        }
      }
      
      // Send message using the message service with sanitized content
      await messageService.sendMessage(
        currentUser.uid,
        recipientId,
        sanitizedMessage,
        [], // No attachments for now
        activeConversation.campaignId
      );
      
      // Clear the input
      setNewMessage('');
      
      // No need to manually add the message to the UI as it will come through the real-time listener
      
    } catch (error) {
      console.error('Error sending message:', error);
      // Display the error message to the user
      showError('Failed to send message.', 'Please check your connection and try again.', 6000);
      
      // Keep the message in the input field so the user can modify it
      // instead of having to retype everything
    }
  };

  // Filter conversations with robust null checks to prevent toLowerCase error on undefined values
  let filteredConversations = [];
  
  try {
    // First ensure conversations is an array
    if (Array.isArray(conversations)) {
      filteredConversations = conversations.filter(conversation => {
        try {
          // Ensure conversation is an object
          if (!conversation) return false;
          
          // Safely extract and check all values
          const brandName = (conversation.brandName || '').toString();
          const campaignTitle = (conversation.campaignTitle || '').toString();
          const searchTermLower = (searchTerm || '').toLowerCase();
          
          return brandName.toLowerCase().includes(searchTermLower) ||
                 campaignTitle.toLowerCase().includes(searchTermLower);
        } catch (innerError) {
          console.error('Error filtering conversation:', innerError, conversation);
          return false; // Skip this conversation if there's an error
        }
      });
    } else {
      console.error('Conversations is not an array:', conversations);
    }
  } catch (outerError) {
    console.error('Fatal error in conversation filtering:', outerError);
    // Return empty array as fallback
  }

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      // Today - show time
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (diffDays === 1) {
      // Yesterday
      return 'Yesterday';
    } else if (diffDays < 7) {
      // This week - show day name
      return date.toLocaleDateString([], { weekday: 'short' });
    } else {
      // Older - show date
      return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
    }
  };

  return (
    <div className="h-[calc(100vh-12rem)]">

      
      <div className="mb-6">
        <h2 className="text-2xl font-semibold text-gray-800 mb-2">Messages</h2>
        <p className="text-gray-600">Communicate with brands about campaigns</p>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden h-full">
        <div className="flex h-full">
          {/* Conversation List */}
          <div className="w-full md:w-1/3 border-r border-gray-200 h-full flex flex-col">
            <div className="p-4 border-b border-gray-200">
              <div className="flex items-center bg-gray-100 rounded-md px-3 py-2">
                <Search className="h-4 w-4 text-gray-500 mr-3 flex-shrink-0" />
                <input
                  type="text"
                  className="block w-full bg-transparent border-none focus:outline-none focus:ring-0 placeholder-gray-500 text-sm"
                  placeholder="Search conversations"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
            
            <div className="overflow-y-auto flex-grow">
              {!loading && filteredConversations.length > 0 ? (
                <ul className="divide-y divide-gray-200">
                  {filteredConversations.map((conversation) => (
                    <li 
                      key={conversation.id}
                      className={`hover:bg-gray-50 cursor-pointer ${
                        activeConversation?.id === conversation.id ? 'bg-gray-50' : ''
                      }`}
                      onClick={() => handleConversationSelect(conversation)}
                    >
                      <div className="px-4 py-3 sm:px-6">
                        <div className="flex items-center space-x-3">
                          {/* Brand avatar */}
                          <div className="flex-shrink-0">
                            <img 
                              src={conversation.brandLogo || '/assets/default-avatar.png'} 
                              alt={conversation.brandName || 'Brand'} 
                              className="h-10 w-10 rounded-full"
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = '/assets/default-avatar.png';
                              }}
                            />
                          </div>
                          
                          {/* Conversation content */}
                          <div className="flex-grow min-w-0">
                            <div className="flex justify-between items-baseline">
                              <p className="text-sm font-medium text-gray-900 truncate">{conversation.brandName}</p>
                              <div className="text-xs text-gray-500 flex-shrink-0 ml-2">
                                {conversation.lastMessage && conversation.lastMessage.timestamp ? 
                                  formatDate(conversation.lastMessage.timestamp) : 
                                  'No messages'}
                              </div>
                            </div>
                            
                            <div className="flex items-center">
                              <p className="text-sm text-gray-600 truncate">
                                {conversation.lastMessage ? (
                                  <>
                                    {/* Only show messages from the brand, not from the current user */}
                                    {conversation.lastMessage.senderId !== currentUser?.uid ? (
                                      conversation.lastMessage.text
                                    ) : (
                                      <span className="italic text-gray-400">You sent a message</span>
                                    )}
                                  </>
                                ) : 'No message content'}
                              </p>
                              {conversation.unreadCount > 0 && (
                                <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-primary text-white flex-shrink-0">
                                  {conversation.unreadCount}
                                </span>
                              )}
                            </div>
                            
                            <p className="text-xs text-gray-500 truncate">
                              {conversation.campaignTitle}
                            </p>
                          </div>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="flex flex-col items-center justify-center h-full p-6 text-center">
                  <CircleUser className="h-12 w-12 text-gray-400 mb-4" />
                  <h3 className="text-sm font-medium text-gray-900 mb-1">No conversations found</h3>
                  <p className="text-sm text-gray-500">
                    {searchTerm ? 'Try adjusting your search' : 'Apply to campaigns to start messaging with brands'}
                  </p>
                </div>
              )}
            </div>
          </div>
          
          {/* Message Area */}
          <div className="hidden md:flex md:w-2/3 flex-col h-full">
            {activeConversation ? (
              <>
                {/* Conversation Header */}
                <div className="px-6 py-3 border-b border-gray-200 flex items-center justify-between">
                  <div className="flex items-center">
                    <img 
                      src={activeConversation.brandLogo || '/assets/default-avatar.png'} 
                      alt={activeConversation.brandName || 'Brand'} 
                      className="h-8 w-8 rounded-full"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = '/assets/default-avatar.png';
                      }}
                    />
                    <div className="ml-3">
                      <p className="text-sm font-medium text-gray-900">{activeConversation.brandName}</p>
                      <p className="text-xs text-gray-500">{activeConversation.campaignTitle}</p>
                    </div>
                  </div>
                  <button className="text-gray-400 hover:text-gray-500">
                    <ChevronDown className="h-5 w-5" />
                  </button>
                </div>
                
                {/* Messages */}
                <div className="flex-grow overflow-y-auto p-4 space-y-4">
                  {messages.map((message) => (
                    <div 
                      key={message.id}
                      className={`flex ${
                        message.senderId === currentUser?.uid ? 'justify-end' : 'justify-start'
                      }`}
                    >
                      <div 
                        className={`max-w-xs sm:max-w-md px-4 py-2 rounded-lg ${
                          message.senderId === currentUser?.uid
                            ? 'bg-primary text-white rounded-br-none'
                            : 'bg-gray-100 text-gray-800 rounded-bl-none'
                        }`}
                      >
                        <div className="text-sm">{message.text}</div>
                        <div 
                          className={`text-xs mt-1 ${
                            message.senderId === currentUser?.uid ? 'text-blue-100' : 'text-gray-500'
                          }`}
                        >
                          {formatDate(message.timestamp)}
                        </div>
                      </div>
                    </div>
                  ))}
                  <div ref={messagesEndRef} />
                </div>
                
                {/* Message Guidelines */}
                <div className="border-t border-gray-200 pt-2 px-4">
                  <button 
                    onClick={() => setShowGuidelines(prev => !prev)} 
                    className="text-xs text-gray-500 hover:text-primary flex items-center mb-2"
                  >
                    {showGuidelines ? 'Hide messaging guidelines' : 'View messaging guidelines'}
                  </button>
                  
                  {showGuidelines && (
                    <div className="mb-3 p-3 bg-blue-50 border border-blue-100 text-sm rounded text-gray-700">
                      <h4 className="font-medium text-primary mb-1">Messaging Guidelines:</h4>
                      <ul className="list-disc pl-5 space-y-1 text-xs">
                        <li>Do not share personal contact information (phone numbers, emails, social media)</li>
                        <li>Avoid sharing external links or website URLs</li>
                        <li>Keep communication professional and respectful</li>
                        <li>All campaign discussions should happen within this platform</li>
                        <li>Report any inappropriate messages to our support team</li>
                      </ul>
                    </div>
                  )}
                
                  {/* Message Input */}
                  <form onSubmit={handleSendMessage} className="flex items-center">
                    <CSRFToken />
                    <button
                      type="button"
                      className="p-2 rounded-full text-gray-400 hover:text-gray-500 focus:outline-none"
                    >
                      <Paperclip className="h-5 w-5" />
                    </button>
                    <input
                      type="text"
                      value={newMessage}
                      onChange={(e) => {
                        setNewMessage(e.target.value);
                        // Error clearing handled by new toast system
                      }}
                      className="flex-grow mx-2 border border-gray-300 rounded-full py-2 px-4 text-sm focus:outline-none focus:ring-primary focus:border-primary"
                      placeholder="Type a message..."
                    />
                    <button
                      type="button"
                      className="p-2 rounded-full text-gray-400 hover:text-gray-500 focus:outline-none"
                    >
                      <Smile className="h-5 w-5" />
                    </button>
                    <button
                      type="submit"
                      disabled={!newMessage.trim()}
                      className={`ml-2 p-2 rounded-full ${
                        newMessage.trim() ? 'bg-primary text-white' : 'bg-gray-200 text-gray-400'
                      } focus:outline-none`}
                    >
                      <Send className="h-5 w-5" />
                    </button>
                  </form>
                </div>
              </>
            ) : (
              <div className="flex flex-col items-center justify-center h-full">
                <div className="h-16 w-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                  <CircleUser className="h-10 w-10 text-gray-400" />
                </div>
                <h3 className="text-sm font-medium text-gray-900 mb-1">Select a conversation</h3>
                <p className="text-sm text-gray-500">Choose a conversation from the list to start messaging</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Messages;
